import EventEmitter from '../Utils/EventEmitter.js'
import { BUILDING_TYPES, BUILD_SITES, DEPOSITS, OSSUARY, RESOURCE_ORDER, TUNING, UNIT_TYPES } from './definitions.js'
import LogisticsSystem from './LogisticsSystem.js'
import CombatSystem from './CombatSystem.js'

const distance = (_a, _b) => Math.hypot(_a.x - _b.x, _a.y - _b.y)

export default class GameState extends EventEmitter
{
    constructor(_save = null)
    {
        super()
        this.elapsed = 0
        this.player = { x: 0, y: 8, stationaryTime: 0 }
        this.inventory = Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0]))
        this.deposits = DEPOSITS.map((_deposit) => ({
            ..._deposit,
            remaining: _deposit.amount,
            output: 0,
            revealed: _deposit.revealWave === 0,
            regrowAt: null
        }))
        this.sites = BUILD_SITES.map((_site) => ({
            ..._site,
            deposited: Object.fromEntries(Object.keys(_site.recipe).map((_resource) => [_resource, 0])),
            built: false,
            revealed: _site.unlockWave === 0
        }))
        this.buildings = []
        this.units = []
        this.ossuary = {
            ...OSSUARY,
            revealed: false,
            stored: Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0]))
        }
        this.completedWaves = 0
        this.mastery = 0
        this.wave = {
            active: false,
            status: 'idle',
            number: 1,
            enemies: [],
            obeliskHealth: TUNING.obeliskHealth,
            obeliskMaxHealth: TUNING.obeliskHealth,
            result: null
        }
        this.gatherCooldown = 0
        this.transferCooldown = 0
        this.action = null
        this.unitSequence = 0
        this.logistics = new LogisticsSystem(this)
        this.combat = new CombatSystem(this)
        this.restore(_save)
    }

    restore(_save)
    {
        if(!_save)
            return
        this.elapsed = _save.elapsed ?? this.elapsed
        this.player.x = _save.player?.x ?? this.player.x
        this.player.y = _save.player?.y ?? this.player.y
        this.inventory = { ...this.inventory, ..._save.inventory }
        for(const deposit of this.deposits)
            Object.assign(deposit, _save.deposits?.[deposit.id] ?? {})
        for(const site of this.sites)
        {
            const saved = _save.sites?.[site.id]
            if(!saved)
                continue
            site.deposited = { ...site.deposited, ...saved.deposited }
            site.built = saved.built ?? site.built
            site.revealed = saved.revealed ?? site.revealed
        }
        if(Array.isArray(_save.buildings))
            this.buildings = _save.buildings
                .filter((_building) => BUILDING_TYPES[_building.type])
                .map((_building) => ({
                    ..._building,
                    inputs: { ...Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0])), ..._building.inputs }
                }))
        if(Array.isArray(_save.units))
            this.units = _save.units.filter((_unit) => UNIT_TYPES[_unit.type])
        this.ossuary.revealed = _save.ossuary?.revealed ?? this.ossuary.revealed
        this.ossuary.stored = { ...this.ossuary.stored, ..._save.ossuary?.stored }
        this.completedWaves = _save.completedWaves ?? this.completedWaves
        this.mastery = _save.mastery ?? this.mastery
        if(_save.wave)
            this.wave = {
                ...this.wave,
                ..._save.wave,
                enemies: Array.isArray(_save.wave.enemies) ? _save.wave.enemies : []
            }
        this.gatherCooldown = _save.gatherCooldown ?? this.gatherCooldown
        this.transferCooldown = _save.transferCooldown ?? this.transferCooldown
        this.unitSequence = _save.unitSequence ?? this.unitSequence
    }

    toJSON()
    {
        return {
            elapsed: this.elapsed,
            player: { x: this.player.x, y: this.player.y },
            inventory: this.inventory,
            deposits: Object.fromEntries(this.deposits.map((_deposit) => [_deposit.id, {
                remaining: _deposit.remaining,
                output: _deposit.output,
                revealed: _deposit.revealed,
                regrowAt: _deposit.regrowAt
            }])),
            sites: Object.fromEntries(this.sites.map((_site) => [_site.id, {
                deposited: _site.deposited,
                built: _site.built,
                revealed: _site.revealed
            }])),
            buildings: this.buildings,
            units: this.units,
            ossuary: { revealed: this.ossuary.revealed, stored: this.ossuary.stored },
            completedWaves: this.completedWaves,
            mastery: this.mastery,
            wave: this.wave,
            gatherCooldown: this.gatherCooldown,
            transferCooldown: this.transferCooldown,
            unitSequence: this.unitSequence
        }
    }

    get activeSite()
    {
        return this.sites.find((_site) => _site.revealed && !_site.built) ?? null
    }

    get inventoryTotal()
    {
        return RESOURCE_ORDER.reduce((_total, _resource) => _total + this.inventory[_resource], 0)
    }

    get combatMultiplier()
    {
        return 1.06 ** this.mastery
    }

    get workerMultiplier()
    {
        return 1.02 ** this.mastery
    }

    setPlayerPosition(_x, _y)
    {
        this.player.x = _x
        this.player.y = _y
    }

    unitCount(_type)
    {
        return this.units.filter((_unit) => _unit.alive && _unit.type === _type).length
    }

    buildingById(_id)
    {
        return this.buildings.find((_building) => _building.id === _id)
    }

    canStartWave()
    {
        return !this.wave.active && Boolean(this.buildingById('guardCrypt')) && !this.activeSite && this.sites
            .filter((_site) => _site.unlockWave <= this.completedWaves)
            .every((_site) => _site.built)
    }

    startNextWave()
    {
        return this.combat.start()
    }

    dismissWaveResult()
    {
        if(this.wave.active)
            return
        this.wave.result = null
        this.trigger('waveDismissed', [])
    }

    update(_delta, _moving)
    {
        this.elapsed += _delta
        this.gatherCooldown = Math.max(0, this.gatherCooldown - _delta)
        this.transferCooldown = Math.max(0, this.transferCooldown - _delta)
        this.player.stationaryTime = _moving ? 0 : this.player.stationaryTime + _delta
        this.action = null

        this.updateDeposits()
        for(const building of this.buildings)
            this.updateBuilding(building, _delta)
        this.logistics.update(_delta)
        this.combat.update(_delta)

        if(this.player.stationaryTime < TUNING.stationaryDelay)
            return
        if(this.tryConstruction())
            return
        if(this.tryBuildingInput())
            return
        if(this.tryBuildingOutput())
            return
        if(this.tryDepositOutput())
            return
        this.tryGathering()
    }

    updateDeposits()
    {
        for(const deposit of this.deposits)
        {
            if(deposit.remaining === 0 && deposit.regrowAt !== null && this.elapsed >= deposit.regrowAt)
            {
                deposit.remaining = deposit.amount
                deposit.regrowAt = null
                this.trigger('depositChanged', [deposit])
                this.trigger('depositRegrown', [deposit])
            }
        }
    }

    updateBuilding(_building, _delta)
    {
        const definition = BUILDING_TYPES[_building.type]
        _building.active = false
        if(definition.kind === 'crypt')
        {
            this.updateCrypt(_building, definition, _delta)
            return
        }
        if(_building.output >= TUNING.outputCapacity)
            return
        if(definition.fuel && _building.fuelRemaining <= 0 && !this.consumeFuel(_building))
            return
        if(_building.progress === 0)
        {
            if((_building.inputs[definition.input] ?? 0) <= 0)
                return
            _building.inputs[definition.input]--
            this.trigger('buildingChanged', [_building])
        }
        _building.active = true
        const step = definition.fuel ? Math.min(_delta, _building.fuelRemaining) : _delta
        _building.progress += step
        if(definition.fuel)
            _building.fuelRemaining = Math.max(0, _building.fuelRemaining - step)
        if(_building.progress < definition.duration)
            return
        _building.progress = 0
        _building.output++
        this.trigger('buildingProduced', [_building])
        this.trigger('buildingChanged', [_building])
    }

    updateCrypt(_building, _definition, _delta)
    {
        if(_building.raisingType)
        {
            _building.active = true
            _building.raiseProgress += _delta
            if(_building.raiseProgress < TUNING.cryptRaiseSeconds)
                return
            const type = _building.raisingType
            _building.raisingType = null
            _building.raiseProgress = 0
            this.spawnUnit(type, _building)
            return
        }

        const count = _definition.units.length
        for(let attempt = 0; attempt < count; attempt++)
        {
            const index = (_building.nextUnitIndex + attempt) % count
            const type = _definition.units[index]
            const definition = UNIT_TYPES[type]
            if(this.unitCount(type) >= definition.cap)
                continue
            const ready = Object.entries(definition.recipe).every(([_resource, _amount]) => (_building.inputs[_resource] ?? 0) >= _amount)
            if(!ready)
                continue
            for(const [resource, amount] of Object.entries(definition.recipe))
                _building.inputs[resource] -= amount
            _building.raisingType = type
            _building.raiseProgress = 0
            _building.nextUnitIndex = (index + 1) % count
            _building.active = true
            this.trigger('buildingChanged', [_building])
            return
        }
    }

    consumeFuel(_building)
    {
        for(const resource of ['souls', 'deadwood'])
        {
            if((_building.inputs[resource] ?? 0) <= 0)
                continue
            _building.inputs[resource]--
            _building.fuelRemaining = TUNING.fuelSeconds[resource]
            this.trigger('buildingChanged', [_building])
            return true
        }
        return false
    }

    spawnUnit(_type, _building)
    {
        const definition = UNIT_TYPES[_type]
        const maximumHealth = (definition.health ?? 1) * (definition.role === 'combat' ? this.combatMultiplier : 1)
        const index = this.unitCount(_type)
        const unit = {
            id: `unit${++this.unitSequence}`,
            type: _type,
            role: definition.role,
            sourceBuildingId: _building.id,
            x: _building.x + (index % 3 - 1) * 0.45,
            y: _building.y - 1.8 - Math.floor(index / 3) * 0.3,
            rotation: 0,
            health: maximumHealth,
            maxHealth: maximumHealth,
            cooldown: 0,
            workCooldown: 0,
            alive: true,
            assignmentId: null,
            destination: null,
            path: [],
            pathIndex: 0,
            task: null,
            carry: null
        }
        this.units.push(unit)
        this.trigger('unitRaised', [{ unit, building: _building }])
        this.trigger('buildingChanged', [_building])
        return unit
    }

    getAcceptedResources(_building)
    {
        const definition = BUILDING_TYPES[_building.type]
        if(definition.kind === 'crypt')
        {
            const resources = new Set()
            for(const type of definition.units)
            {
                const unitDefinition = UNIT_TYPES[type]
                const missing = unitDefinition.cap - this.unitCount(type)
                if(missing <= 0)
                    continue
                for(const [resource, amount] of Object.entries(unitDefinition.recipe))
                {
                    const wanted = Math.min(TUNING.machineSlotCapacity, amount * Math.min(2, missing))
                    if((_building.inputs[resource] ?? 0) < wanted)
                        resources.add(resource)
                }
            }
            return [...resources]
        }

        const resources = []
        if((_building.inputs[definition.input] ?? 0) < TUNING.machineSlotCapacity)
            resources.push(definition.input)
        if(definition.fuel)
        {
            const fuelCount = (_building.inputs.souls ?? 0) + (_building.inputs.deadwood ?? 0)
            if(fuelCount < TUNING.machineSlotCapacity)
                resources.push('souls', 'deadwood')
        }
        return resources
    }

    tryConstruction()
    {
        const site = this.activeSite
        if(!site || Math.abs(this.player.x - site.x) > TUNING.tileHalfSize || Math.abs(this.player.y - site.y) > TUNING.tileHalfSize)
            return false
        if(this.transferCooldown > 0)
            return true
        for(const [resource, required] of Object.entries(site.recipe))
        {
            if(this.inventory[resource] <= 0 || site.deposited[resource] >= required)
                continue
            this.inventory[resource]--
            site.deposited[resource]++
            this.transferCooldown = TUNING.transferInterval
            this.trigger('siteTransfer', [{ site, resource, count: 1 }])
            this.checkSiteCompletion(site)
            break
        }
        return true
    }

    checkSiteCompletion(_site)
    {
        if(_site.built || !Object.entries(_site.recipe).every(([_resource, _required]) => _site.deposited[_resource] >= _required))
            return false
        _site.built = true
        const building = {
            id: _site.id,
            type: _site.buildingType,
            x: _site.x,
            y: _site.y,
            inputs: Object.fromEntries(RESOURCE_ORDER.map((_resource) => [_resource, 0])),
            output: 0,
            fuelRemaining: 0,
            progress: 0,
            active: false,
            raisingType: null,
            raiseProgress: 0,
            nextUnitIndex: 0
        }
        this.buildings.push(building)
        if(building.id === 'porterCrypt')
        {
            this.ossuary.revealed = true
            this.trigger('ossuaryRevealed', [this.ossuary])
        }
        this.trigger('siteCompleted', [{ site: _site, building }])
        this.trigger('waveAvailabilityChanged', [])
        return true
    }

    tryBuildingInput()
    {
        for(const building of this.buildings)
        {
            if(distance(this.player, building) > TUNING.machineRange)
                continue
            const accepted = this.getAcceptedResources(building)
            const resource = accepted.find((_resource) => this.inventory[_resource] > 0)
            if(!resource)
                continue
            if(this.transferCooldown <= 0)
            {
                this.inventory[resource]--
                building.inputs[resource] = (building.inputs[resource] ?? 0) + 1
                this.transferCooldown = TUNING.transferInterval
                this.trigger('buildingInput', [{ building, resource, count: 1 }])
            }
            return true
        }
        return false
    }

    tryBuildingOutput()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return false
        for(const building of this.buildings)
        {
            const definition = BUILDING_TYPES[building.type]
            if(definition.kind !== 'processor' || building.output <= 0 || distance(this.player, building) > TUNING.machineRange)
                continue
            if(this.transferCooldown <= 0)
            {
                building.output--
                this.inventory[definition.output]++
                this.transferCooldown = TUNING.transferInterval
                this.trigger('buildingOutput', [{ building, resource: definition.output }])
            }
            return true
        }
        return false
    }

    tryDepositOutput()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return false
        for(const deposit of this.deposits)
        {
            if(deposit.output <= 0 || distance(this.player, deposit) > TUNING.gatherRange + (deposit.tree ? 0 : 1.15))
                continue
            if(this.transferCooldown <= 0)
            {
                deposit.output--
                this.inventory[deposit.resource]++
                this.transferCooldown = TUNING.transferInterval
                this.trigger('depositOutput', [{ deposit, resource: deposit.resource }])
                this.trigger('depositChanged', [deposit])
            }
            return true
        }
        return false
    }

    tryGathering()
    {
        if(this.inventoryTotal >= TUNING.backpackCapacity)
            return
        const deposit = this.deposits.find((_deposit) =>
            _deposit.revealed && _deposit.remaining > 0 &&
            distance(this.player, _deposit) <= TUNING.gatherRange + (_deposit.tree ? 0 : 1.15)
        )
        if(!deposit)
            return
        this.action = { type: 'channel', deposit }
        if(this.gatherCooldown > 0)
            return
        deposit.remaining--
        this.inventory[deposit.resource]++
        this.gatherCooldown = TUNING.gatherInterval
        if(deposit.remaining === 0)
            deposit.regrowAt = this.elapsed + TUNING.sourceRegrowSeconds
        this.trigger('depositChanged', [deposit])
        this.trigger('gathered', [{ deposit, resource: deposit.resource }])
    }

    transferOssuary(_resource, _direction, _all = false)
    {
        if(!this.ossuary.revealed || distance(this.player, this.ossuary) > 3.2 || !RESOURCE_ORDER.includes(_resource))
            return 0
        const available = _direction === 'deposit' ? this.inventory[_resource] : this.ossuary.stored[_resource]
        const capacity = _direction === 'withdraw' ? TUNING.backpackCapacity - this.inventoryTotal : available
        const count = Math.min(_all ? available : Math.min(1, available), capacity)
        if(count <= 0)
            return 0
        if(_direction === 'deposit')
        {
            this.inventory[_resource] -= count
            this.ossuary.stored[_resource] += count
        }
        else
        {
            this.ossuary.stored[_resource] -= count
            this.inventory[_resource] += count
        }
        this.trigger('ossuaryTransfer', [{ resource: _resource, direction: _direction, count }])
        this.trigger('ossuaryChanged', [this.ossuary])
        return count
    }

    unlockProgress()
    {
        for(const deposit of this.deposits)
        {
            if(deposit.revealed || deposit.revealWave > this.completedWaves)
                continue
            deposit.revealed = true
            this.trigger('depositRevealed', [deposit])
        }
        for(const site of this.sites)
        {
            if(site.revealed || site.unlockWave > this.completedWaves)
                continue
            site.revealed = true
            this.trigger('siteRevealed', [site])
        }
        this.trigger('waveAvailabilityChanged', [])
    }

    shouldGuideToSite(_site)
    {
        return this.activeSite?.id === _site.id
    }

    advanceMilestone()
    {
        const site = this.activeSite
        if(!site)
            return this.canStartWave() ? `Wave ${this.completedWaves + 1} is ready.` : 'No construction site is currently available.'
        for(const [resource, required] of Object.entries(site.recipe))
            site.deposited[resource] = required
        this.checkSiteCompletion(site)
        return `${BUILDING_TYPES[site.buildingType].label} completed.`
    }

    completeWaveForDebug()
    {
        if(!this.wave.active && !this.startNextWave())
            return 'Wave is not ready. Complete the active ritual site first.'
        this.combat.resolve(true)
        return `Wave ${this.completedWaves} completed.`
    }
}
